import { useEffect, useState } from "react";
import { WifiOff, X } from "lucide-react";
import { toast } from "sonner";

/* Surfaces the browser's `navigator.onLine` state to the user. Learners on
   flaky campus / clinic wifi occasionally lose connection mid-session — when
   that happens, the backend calls (sign-in, score save, checkout) will fail
   with the same "no response" pattern as a real backend outage. Distinguishing
   the two matters:
     - Offline (device-side)         → amber banner, temporary, self-resolves
     - Backend unreachable (server)  → red banner, needs support intervention

   navigator.onLine + the `online` / `offline` window events are supported
   universally (IE9+). navigator.onLine is a hint (a false negative is common —
   the browser might report online but the network is actually down). Being
   *offline* according to the browser is a strong signal; being *online* is
   a weak one. We use it here as a strong signal only. */

export function OfflineBanner() {
  const initial = typeof navigator !== "undefined" ? !navigator.onLine : false;
  const [offline, setOffline] = useState(initial);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onOffline = () => {
      setOffline(true);
      setDismissed(false); // A fresh outage un-dismisses.
    };
    const onOnline = () => {
      // Only chirp the recovery toast if we were actually offline (avoid
      // announcing "back online" on cold-load when we never showed the banner).
      setOffline((wasOffline) => {
        if (wasOffline) {
          toast.success("You're back online", {
            description: "Any scores saved locally will sync automatically.",
            duration: 4000,
          });
        }
        return false;
      });
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  if (!offline || dismissed) return null;

  return (
    <div
      className="fixed top-0 inset-x-0 z-[9998] bg-amber-500 text-amber-950 shadow-md border-b border-amber-700"
      data-testid="offline-banner"
      role="status"
      aria-live="polite"
    >
      <div className="max-w-6xl mx-auto px-4 py-2 flex items-start gap-3">
        <WifiOff className="w-5 h-5 shrink-0 mt-0.5" aria-hidden="true" />
        <div className="min-w-0 flex-1 text-sm">
          <div className="font-semibold" data-testid="offline-banner-title">
            You&apos;re offline
          </div>
          <div className="text-amber-900/95 mt-0.5" data-testid="offline-banner-message">
            Simulators still work — signed-in scores are saved locally and will
            sync when your connection returns. Sign-in and payments won&apos;t work until then.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="ml-2 -mr-1 -mt-0.5 p-1.5 rounded hover:bg-amber-600/40 shrink-0"
          data-testid="offline-banner-dismiss"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default OfflineBanner;
